// Shared formatting + URL helpers for the Trade Viewer (scenario browser →
// scenario detail → trade drill-down).

import type { StrategyTrade } from '@/services/strategyService';

// ── Time / duration ──────────────────────────────────────────────

export function fmtNY(isoUtc: string | null | undefined, withSeconds = false): string {
  if (!isoUtc) return '-';
  const d = new Date(isoUtc);
  const opts: Intl.DateTimeFormatOptions = {
    timeZone: 'America/New_York',
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
    ...(withSeconds ? { second: '2-digit' } : {}),
  };
  return new Intl.DateTimeFormat('en-CA', opts).format(d).replace(',', '');
}

/** Compact hold time for dense table rows — "45s" / "12m" / "1.4h". */
export function fmtHold(secs: number): string {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  return `${(secs / 3600).toFixed(1)}h`;
}

/** Richer hold time for stat tiles — "1h 12m" / "12m 30s". */
export function fmtHoldRich(secs: number): string {
  const s = Math.max(0, Math.round(secs));
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r > 0 ? `${m}m ${r}s` : `${m}m`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** "Feb 02" style short date from an ISO/`yyyy-MM-dd` string. */
export function fmtShortDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
  if (isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: '2-digit' }).format(d);
}

/** Coarse "3d ago" / "2mo ago" for card footers. */
export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.round(months / 12)}y ago`;
}

// ── Money / numbers ──────────────────────────────────────────────

/** Signed dollar amount, thousands-separated, no cents — "+$4,182" / "-$610". */
export function fmtMoney(v: number | null | undefined, cents = false): string {
  if (v == null || isNaN(v)) return '-';
  const sign = v > 0 ? '+' : v < 0 ? '-' : '';
  const abs = Math.abs(v);
  return `${sign}$${abs.toLocaleString('en-US', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  })}`;
}

/** Tailwind text colour for a signed value (0 stays neutral). */
export function pnlColor(v: number | null | undefined): string {
  if (v == null || v === 0) return 'text-muted-foreground';
  return v > 0 ? 'text-emerald-500' : 'text-red-500';
}

/** Profit factor can be Infinity when a run has zero losers. */
export function fmtPF(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return '-';
  return isFinite(v) ? v.toFixed(2) : '∞';
}

// ── Trades ───────────────────────────────────────────────────────

// Display-friendly exit label: TakeProfit on positive PnL, StopLoss on negative.
// The underlying exitReason is still shown verbatim in the trade detail header.
export function exitLabel(t: StrategyTrade): string {
  if (t.exitReason == null) return '-';
  const pnl = t.realizedPnL ?? 0;
  if (pnl > 0) return 'TakeProfit';
  if (pnl < 0) return 'StopLoss';
  return t.exitReason;
}

// ── Scenario names ───────────────────────────────────────────────

/**
 * Split "v7 Volume Surge + Percent Based — Feb 02 to May 03" (or
 * "v7b-SHORT ORB + Momentum Runner …") into its parts so a card can show the
 * entry strategy as the title with the run/mgmt as badges. Purely cosmetic —
 * unparseable names fall back to the raw string as the title.
 */
export function parseScenarioName(name: string): { run: string; entry: string; mgmt: string } {
  if (!name) return { run: '', entry: '(unnamed)', mgmt: '' };
  const head = name.split(/\s+[—-]\s+/)[0]?.trim() ?? name;
  const plusIdx = head.indexOf(' + ');
  if (plusIdx < 0) return { run: '', entry: head, mgmt: '' };
  const before = head.slice(0, plusIdx);
  const mgmt = head.slice(plusIdx + 3).trim();
  // First whitespace-delimited token is the run prefix ("v7", "v7b-SHORT", etc.)
  const m = before.match(/^(v\d+\S*)\s+(.+)$/i);
  if (m) return { run: m[1], entry: m[2].trim(), mgmt };
  return { run: '', entry: before.trim(), mgmt };
}

// ── Hash query helpers ───────────────────────────────────────────
// Keep ?scenario=… &trade=… in window.location.hash so shareable URLs land on
// a specific scenario (and optionally a specific trade).

export function readHashQuery(): URLSearchParams {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  return new URLSearchParams(qIdx >= 0 ? hash.slice(qIdx + 1) : '');
}

export function writeHashQuery(params: Record<string, string | null>) {
  const current = readHashQuery();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') current.delete(k);
    else current.set(k, v);
  }
  const hash = window.location.hash;
  const tabPart = hash.split('?')[0] || '#/dashboard/trade-viewer';
  const qs = current.toString();
  const newHash = qs ? `${tabPart}?${qs}` : tabPart;
  window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
}
